import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { ApplicationSectionBaseComponent } from '../../../../../applications/components/application-section-base/application-section-base.component';
import { ApplicationSteps, WorkflowData, ApplicationType } from "../../../../../applications/models/applications.models";
import { AuthService } from "../../../../../services/auth.service";
import { Option } from "../../../../../common/models/flow.dto";
import { PaymentService, AuthorizationRequest, AccountInformation, BillingAddressInformation, PaymentInformation } from "../../../../../payments/services/payment.service";
import { PaymentCalculatorService } from "../../../../../payments/services/payment-calculator.service";
import { LoaderService } from "../../../../../common/services/loader.service";
import { PaymentType, PaymentItem, ApplicationPayment, AuthorizationResponse } from "../../../../../payments/models/paymentItem.model";
import { DynamicDialogService } from "../../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../../core/models/dialog.model";
import { ApplicationsConfig } from "../../../../../services/application-config.service";
import { OrganizationStub } from '../../../../../common/models/organization.model';

@Component({
    selector: 'plat-fees',
    templateUrl: './plat-fees.component.html',
    styleUrls: ['./plat-fees.component.scss', '../../../../../core/components/collapsible/collapsible.component.scss'],
    standalone: false
})
export class PlatFeesComponent extends ApplicationSectionBaseComponent implements OnInit, OnDestroy {

    readonly step: ApplicationSteps = ApplicationSteps.Payment;

    private _config: ApplicationsConfig;

    @Input() application: WorkflowData;

    authorizeDialog: Dialog = new Dialog("You will be referred to an outside payment processor to complete the credit authorization.  Your progress to this point will be saved.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.confirmAuthorize(); }), new DialogButton("No", false, () => { return true; })]);


    @Output() authorizationRequested: EventEmitter<AuthorizationRequest> = new EventEmitter<AuthorizationRequest>();
    @Output() authorizationResponseReceived: EventEmitter<AuthorizationResponse> = new EventEmitter<AuthorizationResponse>();
    @Output() feesGenerated: EventEmitter<PaymentItem[]> = new EventEmitter<PaymentItem[]>();
    @Output() receiptIssued: EventEmitter<ApplicationPayment[]> = new EventEmitter<ApplicationPayment[]>();

    get config(): ApplicationsConfig {
        return this._config;
    }

    @Input() set config(value: ApplicationsConfig) {
        this._config = value;
    }

    options: Option[] = [
        { id: "", text: "" } as Option,
        { id: "none", text: "No fee applicable" } as Option,
        { id: "plat", text: "$50 As Drilled Plat Final Fee" } as Option,
        { id: "revised", text: "$50 Revised Unit Fee" } as Option
    ];

    @Input() organization: OrganizationStub;

    get authorization(): AuthorizationResponse {
        if (this.form == null) return null;
        let ctrl = this.form.get('creditAuthorization');
        if (ctrl == null) return null;
        return <AuthorizationResponse>ctrl.value;
    }

    set authorization(value: AuthorizationResponse) {
        if (this.form == null) return;
        let ctrl = this.form.get('creditAuthorization');
        if (ctrl == null) return;
        ctrl.setValue(value);
        ctrl.updateValueAndValidity();
    }

    get creditAuthorizationRequired(): boolean {
        if (this.selectedPayment == "") return false;
        let type: PaymentType = <PaymentType>this.selectedPayment;
        return type == PaymentType.ACH || type == PaymentType.Credit;
    }

    get disablePaymentButton(): boolean {
        return this.invoiceDocument != null || this.form.get('paymentType').invalid || this.form.get('paymentType').disabled;
    }

    get existingDocumentId(): any {
        if (this.form == null) return null;
        let ctrl = this.form.get('invoice');
        if (ctrl == null) return null;
        return ctrl.value;
    }

    get fees(): ApplicationPayment[] {
        if (this.form == null) return [];
        let ctrl = this.form.get('payments');
        if (ctrl == null) return [];
        return <ApplicationPayment[]>ctrl.value;
    }

    get invoiceDocument(): string {
        if (this.form == null) return null;
        let ctrl = this.form.get('invoice');
        if (ctrl == null) return null;
        return ctrl.value;
    }

    get selectedOption(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('option');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get selectedPayment(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('paymentType');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    private _queryParams: string;

    get showInvoice(): boolean {
        return this._queryParams != null || (this.fees.length > 0 && (this.selectedPayment == PaymentType.LocalCheck || (this.creditAuthorizationRequired && this.invoiceDocument != null)));
    }

    subscription: Subscription = new Subscription();
    

    constructor(auth: AuthService, public route: ActivatedRoute, public paymentService: PaymentService, private calculator: PaymentCalculatorService,
        private dialog: DynamicDialogService, private loader: LoaderService) { super(auth); }

    ngOnInit() {
        let query$ = this.route.queryParams.pipe(
            map((params) => { return params["id"]; }),
        ).subscribe(
            (e: string) => {
                this._queryParams = e;
            },
            (error) => {
                console.error(error);
            }
        );
        this.subscription.add(query$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    buildAuthorization(): Observable<AuthorizationRequest> {
        return this.buildPayments().pipe(
            map((items: PaymentItem[]) => {
                let accountInformation: AccountInformation = new AccountInformation();
                accountInformation.paymentType = this.selectedPayment;
                let billingInformation: BillingAddressInformation = new BillingAddressInformation();
                let paymentInformation = new PaymentInformation();
                paymentInformation.paymentItems = items;
                let req = new AuthorizationRequest(accountInformation, billingInformation, paymentInformation);
                req.applicationType = ApplicationType.ComplianceReporting;
                if (this.application != null) {
                    req.applicationNumber = this.application.applicationNumber;
                }
                return req;
            })
        );       
    }

    buildPayments(): Observable<PaymentItem[]> {
        return this.calculator.calculatePlatFees().pipe(
            map((data: PaymentItem[]) => {
                let type: string = (this.selectedOption == 'plat') ? 'As Drilled Plat Final Fee' : 'Revised Unit Fee';
                data.forEach(d => d.description = type);
                return data;
            })
        );
    }

    confirmAuthorize(): void {
        let obs$: Observable<any> = of(null);
        if (this.creditAuthorizationRequired) {
            obs$ = this.buildAuthorization().pipe(tap((req: AuthorizationRequest) => {
                this.authorizationRequested.emit(req);
            }));
        } else if (this.selectedPayment == PaymentType.LocalCheck) {
            obs$ = this.buildPayments().pipe(tap((pmts: PaymentItem[]) => {
                this.feesGenerated.emit(pmts);
            }))
        }
        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe().subscribe(
            () => { this.loader.toggleLoader(false); },
            (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
                this.dialog.showError();
            }
        );
        this.subscription.add(subs$);
    }

    onReceiptIssued(pmts: ApplicationPayment[]): void {
        this.receiptIssued.emit(pmts);
    }

    paymentOptionChanged(option: string): void {
        let typeCtrl = this.form.get('paymentType');
        let authCtrl = this.form.get('creditAuthorization');
        if (option == 'none' || option == '') {
            typeCtrl.clearValidators();
            typeCtrl.setValue('');
            typeCtrl.disable();
            authCtrl.clearValidators();
        } else if (option == 'plat' || option == 'revised') {
            typeCtrl.enable();
            typeCtrl.setValidators([Validators.required]);
        }
        typeCtrl.updateValueAndValidity();
        authCtrl.updateValueAndValidity();
    }

    paymentTypeChanged(type: string): void {
        let authCtrl = this.form.get('creditAuthorization');
        let invoiceCtrl = this.form.get('invoice');
        if (this.creditAuthorizationRequired) {
            authCtrl.setValidators([Validators.required]);
            invoiceCtrl.setValidators([Validators.required]);
        } else if (type == PaymentType.LocalCheck) {
            authCtrl.clearValidators();
            invoiceCtrl.setValidators([Validators.required]);
        } else {
            authCtrl.clearValidators();
            invoiceCtrl.clearValidators();
        }
        authCtrl.updateValueAndValidity();
        invoiceCtrl.updateValueAndValidity();
    }

    requestAuthorization(): void {
        if (this.creditAuthorizationRequired) {
            this.authorizeDialog.toggleDialog(true);
            this.dialog.updateDialog(this.authorizeDialog);
        } else {
            this.confirmAuthorize();
        }
    }

    saveNewResponse(response: AuthorizationResponse): void {
        response.DateAuthorized = response.DateAuthorized.replace(/\+/g, ' ')
        this.authorization = response;
        this.authorizationResponseReceived.emit(response);
    }

}
